import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, fetchJson } from "@/lib/queryClient";
import type { RemTaxa, RemTaxaSetor, RemAuxilioRecorrente } from "@shared/schema";
import type { LinhaDePessoa, SetorDaPessoa } from "@shared/remuneracao-calculo";

// ─── Taxas ──────────────────────────────────────────────────────────────────
// Toda taxa tem VIGÊNCIA — mas "vigência" e "usada" não são a mesma coisa.
// O que fica congelado é o mês JÁ PAGO (fechamento_mensal), não o cadastro
// recém-digitado: enquanto nenhum fechamento consumiu a taxa, editar ou
// apagar corrige um erro de digitação, não finge um reajuste que não houve.

/** `usadaEm` ('YYYY-MM') = já foi consumida por um fechamento — a tela mostra como somente leitura. */
export type RemTaxaComUso = RemTaxa & { usadaEm: string | null };

/** Erro de negócio do módulo — extrai `code`/campos extras do corpo JSON lançado por apiRequest. */
export function parseErroRemuneracao(e: unknown, fallback = "Tente novamente."): {
  message: string; code?: string; usadaEm?: string;
} {
  const bruto = e instanceof Error ? e.message : String(e ?? "");
  const m = /^\d{3}:\s*([\s\S]*)$/.exec(bruto);
  const corpo = m ? m[1] : bruto;
  try {
    const j = JSON.parse(corpo);
    if (j && typeof j.error === "string") {
      return { message: j.error, code: j.code, usadaEm: j.usadaEm };
    }
  } catch {
    /* corpo não é JSON — usa o texto cru */
  }
  return { message: corpo || fallback };
}

export function useTaxasRemuneracao() {
  return useQuery<RemTaxaComUso[]>({
    queryKey: ["/api/equipe/remuneracao/taxas"],
    queryFn: () => fetchJson<RemTaxaComUso[]>("/api/equipe/remuneracao/taxas"),
  });
}

export function useCreateTaxaRemuneracao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { vigenciaInicio: string; valorFixoHa: string; valorVariavelHa: string }) =>
      apiRequest("POST", "/api/equipe/remuneracao/taxas", data).then(r => r.json() as Promise<RemTaxa>),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/equipe/remuneracao/taxas"] }),
  });
}

/** Só valores — nunca a vigência. NUNCA recusa por já ter sido usada — a tela confirma antes de chamar (ver `usadaEm`). */
export function useUpdateTaxaRemuneracao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { valorFixoHa?: string; valorVariavelHa?: string } }) =>
      apiRequest("PATCH", `/api/equipe/remuneracao/taxas/${id}`, data).then(r => r.json() as Promise<RemTaxa>),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/equipe/remuneracao/taxas"] }),
  });
}

/** NUNCA recusa por já ter sido usada — a tela confirma (com aviso mais forte) antes de chamar. */
export function useDeleteTaxaRemuneracao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/equipe/remuneracao/taxas/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/equipe/remuneracao/taxas"] }),
  });
}

export function useTaxaSetoresRemuneracao(remTaxaId: string | undefined) {
  return useQuery<RemTaxaSetor[]>({
    queryKey: ["/api/equipe/remuneracao/taxas", remTaxaId, "setores"],
    queryFn: () => fetchJson<RemTaxaSetor[]>(`/api/equipe/remuneracao/taxas/${remTaxaId}/setores`),
    enabled: !!remTaxaId,
  });
}

export type ExcecaoTaxaSetor = {
  setorId: string;
  valorFixoHa: string | null;
  valorVariavelHa: string | null;
  observacao: string;
};

/** Substitui as exceções INTEIRAS da vigência de uma vez. */
export function useReplaceTaxaSetoresRemuneracao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ remTaxaId, excecoes }: { remTaxaId: string; excecoes: ExcecaoTaxaSetor[] }) =>
      apiRequest("PUT", `/api/equipe/remuneracao/taxas/${remTaxaId}/setores`, { excecoes })
        .then(r => r.json() as Promise<RemTaxaSetor[]>),
    onSuccess: (_data, { remTaxaId }) =>
      qc.invalidateQueries({ queryKey: ["/api/equipe/remuneracao/taxas", remTaxaId, "setores"] }),
  });
}

// ─── Auxílios recorrentes — a REGRA (o lançamento do mês é tarefa_adicional) ──

export function useAuxiliosRecorrentesRemuneracao(funcionarioId?: string) {
  const url = funcionarioId
    ? `/api/equipe/remuneracao/auxilios/recorrentes?funcionarioId=${funcionarioId}`
    : "/api/equipe/remuneracao/auxilios/recorrentes";
  return useQuery<RemAuxilioRecorrente[]>({
    queryKey: ["/api/equipe/remuneracao/auxilios/recorrentes", funcionarioId ?? null],
    queryFn: () => fetchJson<RemAuxilioRecorrente[]>(url),
  });
}

export function useCreateAuxilioRecorrenteRemuneracao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { funcionarioId: string; descricao: string; valor: string; observacao?: string | null }) =>
      apiRequest("POST", "/api/equipe/remuneracao/auxilios/recorrentes", data).then(r => r.json() as Promise<RemAuxilioRecorrente>),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/equipe/remuneracao/auxilios/recorrentes"] }),
  });
}

export function useUpdateAuxilioRecorrenteRemuneracao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<{ descricao: string; valor: string; ativo: boolean; observacao: string | null }> }) =>
      apiRequest("PATCH", `/api/equipe/remuneracao/auxilios/recorrentes/${id}`, data).then(r => r.json() as Promise<RemAuxilioRecorrente>),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/equipe/remuneracao/auxilios/recorrentes"] }),
  });
}

/** Soft delete — desliga o recorrente (não apaga histórico dos lançamentos já gerados). */
export function useDeleteAuxilioRecorrenteRemuneracao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/equipe/remuneracao/auxilios/recorrentes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/equipe/remuneracao/auxilios/recorrentes"] }),
  });
}

/** Gera os lançamentos que faltam na competência da quinzena — idempotente, nunca sobrescreve. */
export function useGerarAuxiliosRemuneracao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (quinzenaId: string) =>
      apiRequest("POST", "/api/equipe/remuneracao/auxilios/gerar", { quinzenaId }).then(r => r.json() as Promise<{ criados: number }>),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/equipe/quinzenas"] });
      qc.invalidateQueries({ queryKey: ["/api/equipe/fechamentos"] });
    },
  });
}

// ─── Simulação — prévia por pessoa, nunca gravada. Nunca vê nota: consome ──
// a nota ÚNICA e os percentuais já resolvidos por GET /api/auditoria/resultados.
// ⚠️ REVERSÃO (03/09/2026): nota da pessoa voltou a ser única — não existe
// mais dinheiro por setor, só a memória de cálculo (`setores`, sem R$).

export type SetorDaPessoaRemuneracao = SetorDaPessoa;

export type PessoaSimulacaoRemuneracao = LinhaDePessoa & {
  fixo: string;
  variavel: string;
};

export type SimulacaoRemuneracao = {
  competencia: string;
  taxa: RemTaxa;
  porPessoa: PessoaSimulacaoRemuneracao[];
};

export function useSimulacaoRemuneracao(competencia: string | undefined) {
  return useQuery<SimulacaoRemuneracao>({
    queryKey: ["/api/equipe/remuneracao/simulacao", competencia],
    queryFn: () => fetchJson<SimulacaoRemuneracao>(`/api/equipe/remuneracao/simulacao?competencia=${competencia}`),
    enabled: !!competencia,
    retry: false,
  });
}
